import React, { useEffect, useState } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import AvisClientsCarousel from '../components/AvisClientsCarousel';
import VehicleCard from '../components/VehicleCard';
import { getVehiclesPublic } from '../services/api';

function Vehicles() {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [filters, setFilters] = useState({
    etat: '',
    carburant: '',
    transmission: '',
    marque: '',
    anneeMin: '',
    anneeMax: '',
    prixMin: '',
    prixMax: '',
    kmMin: '',
    kmMax: '',
  });

  useEffect(() => {
    getVehiclesPublic()
      .then(res => setVehicles(res.data))
      .catch(err => console.error(err))
      .finally(() => setLoading(false));
  }, []);

  const marques = [...new Set(vehicles.map(v => v.marque).filter(Boolean))];

  const handleFilterChange = (e) => {
    setFilters({ ...filters, [e.target.name]: e.target.value });
  };

  const resetFilters = () => {
    setFilters({
      etat: '',
      carburant: '',
      transmission: '',
      marque: '',
      anneeMin: '',
      anneeMax: '',
      prixMin: '',
      prixMax: '',
      kmMin: '',
      kmMax: '',
    });
    setSearch('');
  };

  const filtered = vehicles.filter(v => {
    const searchLower = search.toLowerCase();
    const matchSearch = !search ||
      (v.marque && v.marque.toLowerCase().includes(searchLower)) ||
      (v.modele && v.modele.toLowerCase().includes(searchLower)) ||
      (v.carburant && v.carburant.toLowerCase().includes(searchLower)) ||
      (v.couleur && v.couleur.toLowerCase().includes(searchLower)) ||
      (v.transmission && v.transmission.toLowerCase().includes(searchLower));

    const matchEtat = !filters.etat || v.etat === filters.etat;
    const matchCarburant = !filters.carburant || v.carburant === filters.carburant;
    const matchTransmission = !filters.transmission || v.transmission === filters.transmission;
    const matchMarque = !filters.marque || v.marque === filters.marque;
    const matchAnneeMin = !filters.anneeMin || v.annee >= parseInt(filters.anneeMin);
    const matchAnneeMax = !filters.anneeMax || v.annee <= parseInt(filters.anneeMax);
    const matchPrixMin = !filters.prixMin || v.prix >= parseInt(filters.prixMin);
    const matchPrixMax = !filters.prixMax || v.prix <= parseInt(filters.prixMax);
    const matchKmMin = !filters.kmMin || v.kilometrage >= parseInt(filters.kmMin);
    const matchKmMax = !filters.kmMax || v.kilometrage <= parseInt(filters.kmMax);

    return matchSearch && matchEtat && matchCarburant && matchTransmission &&
      matchMarque && matchAnneeMin && matchAnneeMax && matchPrixMin && matchPrixMax && matchKmMin && matchKmMax;
  });

  return (
    <div className="bg-white min-h-screen">
      <Header />

      <div className="max-w-7xl mx-auto px-4 py-12">
        <h1 className="text-black text-4xl font-bold mb-2">
          Nos <span className="text-yellow-500">vehicules</span>
        </h1>
        <p className="text-gray-400 mb-8">Trouvez le vehicule qui vous correspond</p>

        {/* Barre de recherche */}
        <div className="flex gap-3 mb-4">
          <div className="flex-1 relative">
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Rechercher par marque, modele, carburant..."
              className="w-full border-2 border-gray-200 rounded-xl px-5 py-3 focus:outline-none focus:border-yellow-500 transition text-black"
            />
            {search && (
              <button
                onClick={() => setSearch('')}
                className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-400 hover:text-black"
              >
                x
              </button>
            )}
          </div>
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`px-6 py-3 rounded-xl font-bold border-2 transition ${
              showFilters
                ? 'bg-yellow-500 text-black border-yellow-500'
                : 'border-gray-200 text-gray-600 hover:border-yellow-500 hover:text-yellow-600'
            }`}
          >
            Filtres
          </button>
        </div>

        {/* Filtres avances */}
        {showFilters && (
          <div className="bg-gray-50 border border-gray-200 rounded-2xl p-6 mb-8">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">

              <div>
                <label className="text-gray-600 text-sm font-semibold mb-1 block">Marque</label>
                <select name="marque" value={filters.marque} onChange={handleFilterChange} className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-500 text-black">
                  <option value="">Toutes</option>
                  {marques.map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </div>

              <div>
                <label className="text-gray-600 text-sm font-semibold mb-1 block">Etat</label>
                <select name="etat" value={filters.etat} onChange={handleFilterChange} className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-500 text-black">
                  <option value="">Tous</option>
                  <option value="Neuf">Neuf</option>
                  <option value="Occasion">Occasion</option>
                </select>
              </div>

              <div>
                <label className="text-gray-600 text-sm font-semibold mb-1 block">Carburant</label>
                <select name="carburant" value={filters.carburant} onChange={handleFilterChange} className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-500 text-black">
                  <option value="">Tous</option>
                  <option value="Essence">Essence</option>
                  <option value="Diesel">Diesel</option>
                  <option value="Electrique">Electrique</option>
                  <option value="Hybride">Hybride</option>
                </select>
              </div>

              <div>
                <label className="text-gray-600 text-sm font-semibold mb-1 block">Transmission</label>
                <select name="transmission" value={filters.transmission} onChange={handleFilterChange} className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-500 text-black">
                  <option value="">Toutes</option>
                  <option value="Manuelle">Manuelle</option>
                  <option value="Automatique">Automatique</option>
                </select>
              </div>

              <div>
                <label className="text-gray-600 text-sm font-semibold mb-1 block">Annee min</label>
                <input type="number" name="anneeMin" value={filters.anneeMin} onChange={handleFilterChange} placeholder="Ex: 2018" className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-500 text-black" />
              </div>

              <div>
                <label className="text-gray-600 text-sm font-semibold mb-1 block">Annee max</label>
                <input type="number" name="anneeMax" value={filters.anneeMax} onChange={handleFilterChange} placeholder="Ex: 2024" className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-500 text-black" />
              </div>

              <div>
                <label className="text-gray-600 text-sm font-semibold mb-1 block">Prix min (€)</label>
                <input type="number" name="prixMin" value={filters.prixMin} onChange={handleFilterChange} placeholder="Ex: 5000" className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-500 text-black" />
              </div>

              <div>
                <label className="text-gray-600 text-sm font-semibold mb-1 block">Prix max (€)</label>
                <input type="number" name="prixMax" value={filters.prixMax} onChange={handleFilterChange} placeholder="Ex: 30000" className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-500 text-black" />
              </div>

              <div>
                <label className="text-gray-600 text-sm font-semibold mb-1 block">Kilométrage min (km)</label>
                <input type="number" name="kmMin" value={filters.kmMin} onChange={handleFilterChange} placeholder="Ex: 0" className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-500 text-black" />
              </div>

              <div>
                <label className="text-gray-600 text-sm font-semibold mb-1 block">Kilométrage max (km)</label>
                <input type="number" name="kmMax" value={filters.kmMax} onChange={handleFilterChange} placeholder="Ex: 100000" className="w-full border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-yellow-500 text-black" />
              </div>
            </div>

            <div className="flex justify-between items-center mt-4">
              <p className="text-gray-500 text-sm">{filtered.length} vehicule(s) trouve(s)</p>
              <button onClick={resetFilters} className="text-red-400 hover:text-red-600 font-semibold text-sm transition">
                Reinitialiser les filtres
              </button>
            </div>
          </div>
        )}

        {loading ? (
          <div className="text-center text-yellow-500 text-xl py-20">Chargement...</div>
        ) : filtered.length === 0 ? (
          <div className="text-center text-gray-500 py-20">
            <p className="text-2xl mb-2">Aucun vehicule trouve</p>
            <p className="text-sm">Essayez de modifier vos criteres de recherche</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {filtered.map(vehicle => (
              <VehicleCard key={vehicle.id} vehicle={vehicle} />
            ))}
          </div>
        )}
      </div>

      <AvisClientsCarousel />
      <Footer />
    </div>
  );
}

export default Vehicles;